<template>
  <RightSideBar
    @submit="handleSubmit"
    @close="$emit('close')"
    @back="$emit('close')"
    :submit="submitButtonText"
    :cancel="'Cancel'"
    :loading="isLoading"
    :back-button="true"
    button-class="bg-dynamicBackBtn text-white btnClass rounded-full"
  >
    <template v-slot:title>
      <div class="flex justify-start items-center w-full">
        <p class="text-darkPurple font-bold text-lg leading-5 p-0 -ml-2">
          Pay Group
        </p>

        <div @click="handleBack" v-if="currentStep === 2" class="bg-#F1F1F1  rounded-full p-1  flex items-center justify-center ml-auto p-2 cursor-pointer" style="background-color: #F1F1F1;">
          <Icon icon-name="settings" size="m" class-name="text-flame" />
        </div>
      </div>
    </template>

    <div v-if="currentStep === 1" :key="`step-1-${stepKey}`" class="px-1">
      <div class="info-box text-gray-700 font-medium bg-[#E9932314] border border-orange-200 rounded-lg p-5 mb-6">
        <div class="flex items-start gap-3 w-full">
          <div class="flex items-center justify-center">
            <Icon icon-name="bulb-alert"  class="" style="fill: transparent;width: 24px; height: 24px;"/>

          </div>
          <div v-if="showInformationBox" class=" leading-relaxed">
            <p class="mb-3 text-[12px]">
              Multiple pay groups enables you to create and manage different pay structures for employees,
              including salary, allowances, deductions, and pay cycles, based on employment type, job level,
              organizational unit, or location.
            </p>
            <p class="mb-3 text-[12px]">
              If your organization hires independent contractors, you'll need to create a separate pay group
              for them. Employees designated as independent contractors will automatically inherit this group,
              regardless of their job level, unit, or location.
            </p>
            <p class="mb-0 text-[12px]">
              You must enable this setting before you can profile employees as independent contractors.
            </p>
          </div>
          <div v-else class="text-[12px] truncate mt-1">Multiple pay groups enables you to create and manage...</div>
          <div @click="toggleInformationBox" class="cursor-pointer ml-auto">
            <Icon icon-name="chevronDown" class="w-7 h-7" />
          </div>
        </div>
      </div>

      <div class="grid grid-cols-2 gap-4 mb-6 mt-6">
        <div
          v-for="criteria in criteriaOptions"
          :key="criteria.id"
          class="flex flex-col  gap-[12px] rounded-lg p-4 cursor-pointer hover:border-orange-400 hover:bg-orange-50 transition-all duration-200"
          @click="selectedCriteria = criteria.id"
          style="background-color: #F9F9F9;height: 100px;"
        >
          <div class="flex justify-between items-start bg-[#F9F9F9] mb-3 ">
              <img :src="require(`@/assets/${criteria.src}`)" alt="criteria.name" class="" />
            <div class="w-5 h-5 border-2 border-gray-300 rounded-full flex items-center justify-center flex-shrink-0"
                 :class="{ 'bg-dynamicBackBtn': selectedCriteria === criteria.id }">
              <div v-if="selectedCriteria === criteria.id" class="w-1.5 h-1.5 bg-white rounded-full"></div>
            </div>
          </div>
          <h3 class="font-[400] text-gray-800 text-[14px] leading-tight">{{ criteria.name }}</h3>
        </div>
      </div>

      <div class="flex items-center gap-3 mb-6 mt-10 border-t border-gray-200 ">
        <label class="switch">
          <input
            type="checkbox"
            :checked="independentContractorEnabled"
            @change="independentContractorEnabled = $event.target.checked"
          />
          <span class="slider round"></span>
        </label>
        <p class="text-lg font-semibold text-gray-700">Independent Contractor Settings</p>
      </div>


    </div>

    <div v-if="currentStep === 2" :key="`step-2-${stepKey}`" class="px-1">
      <div class="mb-6">
        <p class="text-sm font-bold text-darkPurple mb-1">
          Select <span class="text-flame">{{ getCriteriaName() }}</span> that this settings apply to:
        </p>
      </div>

      <div class="space-y-5">
        <div v-for="setting in existingPaySettings.filter(s => s.name !== 'Independent Contractor')" :key="setting.id" class="border border-gray-100 rounded-[16px] p-5 bg-white" style="border-color: #FFFFFF;border-radius: 16px;" >
          <div class="flex items-center justify-between cursor-pointer mb-4" @click="toggleSetting(setting)">
            <h4 class="text-sm font-bold text-orange-600 uppercase" >{{setting.name}}</h4>
            <div class="flex-1 h-[1px] px-2" style="height: 1px; background-color: #0000001F;"></div>
            <div class="flex items-center gap-2">
              <div
                v-if="hasNoSelectedOptions(setting)"
                @click.stop="handleDeleteSetting(setting)"
                class="cursor-pointer text-flame hover:text-red-600 transition-colors duration-200 flex items-center"
              >
                <Icon
                  icon-name="delete"
                  size="s"
                  class-name="text-flame"
                />
              </div>
              <Icon
                icon-name="chevronDown"
                size="xs"
                :class-name="`cursor-pointer opacity-70 hover:opacity-100 transition-transform duration-200 ${isSettingExpanded(setting) ? 'rotate-180' : ''}`"
              />
            </div>
          </div>

          <div v-if="isSettingExpanded(setting)">
            <div class="mb-4">
              <label class="block text-sm font-semibold text-gray-700 mb-2">
                Name
              </label>
              <c-text
                v-model="setting.name"
                placeholder="Enter Name"
                class="w-full"
              />
            </div>

            <div class="border-t border-gray-200 pt-7">
              <label class="block text-m font-extrabold text-jet mb-3 uppercase tracking-wider">
                Apply To
              </label>
              <div class=" max-h-72 overflow-y-auto pr-2 custom-scrollbar">
                <label
                  v-for="option in getCriteriaOptions()"
                  :key="option.id"
                  class="flex items-center py-1 px-3 rounded-md transition-colors duration-150"
                  :class="{
                    'hover:bg-orange-50 cursor-pointer': !isOptionDisabledForSetting(option.id, setting.id),
                    'opacity-50 cursor-not-allowed ': isOptionDisabledForSetting(option.id, setting.id)
                  }"
                >

                <div class="flex items-center justify-between flex-1">
                  <checkbox
                    :label="option.name"
                    v-model="setting.groupIds"
                    :value="option.id"
                    :disabled="isOptionDisabledForSetting(option.id, setting.id)"
                    label-style="color:#333333"
                    checkbox-size="height:16px; width:16px; margin-top: 1px;"
                  />
                </div>
                </label>
              </div>
            </div>
          </div>
        </div>

        <!-- New Pay Group Form (Only show when not in editing mode) -->
        <div v-if="!isEditingMode" class="border border-gray-200 rounded-lg p-5 bg-white">

          <div class="flex items-center justify-between cursor-pointer mb-4" @click="togglePaySettingForm">
            <h4 class="text-sm font-bold text-orange-600 uppercase" >PAY GROUP {{ paySettingsCount }}</h4>
            <div class="flex-1 h-[1px] px-2" style="height: 1px; background-color: #0000001F;"></div>
            <div class="flex items-center">
              <Icon
                icon-name="chevronDown"
                size="xs"
                :class-name="`cursor-pointer opacity-70 hover:opacity-100 transition-transform duration-200 ${isPaySettingFormExpanded ? 'rotate-180' : ''}`"
              />
            </div>
          </div>

          <div  class="mb-4">
            <label class="block text-sm font-semibold text-gray-700 mb-2">
              Name
            </label>
            <input
              v-model="paySettingName"
              type="text"
              :placeholder="`Pay Group ${paySettingsCount}`"
              class="w-full px-4 py-2.5 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-orange-500 focus:border-transparent transition-all"
            />
          </div>

          <div v-if="isPaySettingFormExpanded" class="border-t border-gray-200 pt-4">
            <label class="block text-sm font-semibold text-gray-700 mb-3 uppercase tracking-wider">
              Apply To
            </label>
            <div class="max-h-72 overflow-y-auto pr-2 custom-scrollbar">
              <label
                v-for="option in getCriteriaOptions()"
                :key="option.id"
                class="flex items-center py-1 px-3 rounded-md transition-colors duration-150"
                :class="{
                  'hover:bg-orange-50 cursor-pointer': !isOptionDisabled(option.id),
                  'opacity-50 cursor-not-allowed': isOptionDisabled(option.id)
                }"
              >
               <checkbox
                :label="option.name"
                v-model="selectedOptions"
                :value="option.id"
                :disabled="isOptionDisabled(option.id)"
                label-style="color:#333333"
                checkbox-size="height:16px; width:16px; margin-top: 1px;"
               />
              </label>
            </div>
          </div>
        </div>
      </div>
    </div>
  </RightSideBar>
</template>

<script>
import RightSideBar from "@/components/RightSideBar";
import Icon from "@/components/Icon";
import CText from "@scelloo/cloudenly-ui/src/components/text";
import Checkbox from "@/components/Checkbox";

export default {
  components: {
    RightSideBar,
    Icon,
    CText,
    Checkbox
  },
  data() {
    return {
      currentStep: 1,
      stepKey: 0,
      preventWatcherReset: false,
      isLoading: false,
      selectedCriteria: 'employmentType',
      independentContractorEnabled: false,
      showInformationBox: false,
      isPaySettingFormExpanded: true,
      paySettingName: '',
      selectedOptions: [],
      existingPaySettings: [],
      editingPaySetting: null,
      expandedSettings: {},
      optionsData: {
        employmentType: [],
        jobLevel: [],
        organizationalUnit: [],
        location: []
      },
      criteriaOptions: [
        {
          id: 'employmentType',
          value: 'employment-type',
          name: 'Employment Type',
          src: 'employmentType.svg'
        },
        {
          id: 'jobLevel',
          value: 'job-level',
          name: 'Job Level',
          src: 'jobType.svg'
        },
        {
          id: 'organizationalUnit',
          value: 'organizational-unit',
          name: 'Organizational Unit',
          src: 'organizationalUnit.svg'
        },
        {
          id: 'location',
          value: 'location',
          name: 'Location',
          src: 'location.svg'
        }
      ]
    };
  },
  props: {
    existingSettings: {
      type: Array,
      default: () => []
    },
    editingSetting: {
      type: Object,
      default: null
    },
    defaultCriteria: {
      type: String,
      default: null
    },
    isEditingMode: {
      type: Boolean,
      default: false
    }
  },
  computed: {
    submitButtonText() {
      if (this.isEditingMode) {
        return 'Update';
      }
      if (this.currentStep === 1 && this.independentContractorEnabled) {
        return 'Save';
      }
      return this.currentStep === 1 ? 'Next' : 'Save';
    },
    disabledOptionIds() {
      const disabledIds = [];
      this.existingPaySettings.forEach(setting => {
        if (setting.groupIds && setting.id !== this.editingPaySetting?.id) {
          disabledIds.push(...setting.groupIds);
        }
      });
      if (this.selectedOptions && this.selectedOptions.length > 0) {
        disabledIds.push(...this.selectedOptions);
      }
      return disabledIds;
    },
    paySettingsCount() {
      const count = this.existingPaySettings.filter(s => s.name !== 'Independent Contractor').length;
      return count + 1;
    }
  },
  watch: {
    existingSettings: {
      handler(newVal) {
        if (this.preventWatcherReset) return;
        this.initializePaySettings(newVal || []);

        const hasIndependentContractor = this.existingPaySettings.some(s => s.name === 'Independent Contractor');
        if (hasIndependentContractor) {
          this.independentContractorEnabled = true;
        }

        if (this.existingPaySettings.length > 0) {
          const settingWithGroup = this.existingPaySettings.find(s => s.group && s.name !== 'Independent Contractor');

          if (settingWithGroup) {
            const groupToCriteria = {
              'employment-type': 'employmentType',
              'job-level': 'jobLevel',
              'organizational-unit': 'organizationalUnit',
              'location': 'location'
            };
            const newCriteria = groupToCriteria[settingWithGroup.group] || this.selectedCriteria;
            if (this.selectedCriteria !== newCriteria) {
              this.selectedCriteria = newCriteria;

              if (!this.isEditingMode && this.currentStep === 1) {
                this.$nextTick(() => {
                  this.stepKey += 1;
                  this.$forceUpdate();
                });
              }
            }
          }

          if (this.isEditingMode) {
            this.$nextTick(() => {
              this.currentStep = 2;
            });
          } else if (settingWithGroup) {
            this.$nextTick(() => {
              this.currentStep = 2;
              this.paySettingName = `Pay Group ${this.paySettingsCount}`;
              if (!hasIndependentContractor) {
                this.independentContractorEnabled = false;
              }
            });
          }
        }
      },
      immediate: true
    },
    editingSetting: {
      handler(newVal) {
        this.editingPaySetting = newVal;
        if (newVal) {
          this.paySettingName = newVal.name;
          if (newVal.group) {
            const groupToCriteria = {
              'employment-type': 'employmentType',
              'job-level': 'jobLevel',
              'organizational-unit': 'organizationalUnit',
              'location': 'location'
            };
            this.selectedCriteria = groupToCriteria[newVal.group] || 'employmentType';
          }
          let groupIds = newVal.groupIds || [];
          if (!Array.isArray(groupIds)) {
            groupIds = groupIds ? [].concat(groupIds) : [];
          }
          if (groupIds.length === 0 && newVal.groupValues && Array.isArray(newVal.groupValues) && newVal.groupValues.length > 0) {
            groupIds = newVal.groupValues.map(item => {
              if (typeof item === 'object' && item !== null && item.id) {
                return String(item.id);
              }
              return String(item);
            }).filter(Boolean);
          }

          groupIds = groupIds.map(id => {
            if (typeof id === 'object' && id !== null && id.id) {
              return String(id.id);
            }
            return String(id);
          });

          this.selectedOptions = groupIds;
          const hasIndependentContractor = this.existingPaySettings.some(s => s.name === 'Independent Contractor');
          if (hasIndependentContractor) {
            this.independentContractorEnabled = true;
          } else {
            this.independentContractorEnabled = false;
          }
        }
      },
      immediate: true
    },
    defaultCriteria: {
      handler(newVal) {
        if (newVal && !this.editingPaySetting) {
          this.selectedCriteria = newVal;
        }
      },
      immediate: true
    },
    isEditingMode: {
      handler(newVal) {
        if (this.preventWatcherReset) return;

        if (newVal) {
          const hasIndependentContractor = this.existingPaySettings.some(s => s.name === 'Independent Contractor');
          if (hasIndependentContractor) {
            this.independentContractorEnabled = true;
          }

          this.$nextTick(() => {
            this.currentStep = 2;
            let group = null;
            if (this.existingPaySettings.length > 0 && this.existingPaySettings[0].group) {
              group = this.existingPaySettings[0].group;
            } else if (this.editingPaySetting && this.editingPaySetting.group) {
              group = this.editingPaySetting.group;
            }

            if (group) {
              const groupToCriteria = {
                'employment-type': 'employmentType',
                'job-level': 'jobLevel',
                'organizational-unit': 'organizationalUnit',
                'location': 'location'
              };
              this.selectedCriteria = groupToCriteria[group] || 'employmentType';
            }
          });
        } else {
          this.currentStep = 1;
          this.$nextTick(() => {
            if (this.existingPaySettings.length > 0) {
              const settingWithGroup = this.existingPaySettings.find(s => s.group && s.name !== 'Independent Contractor');
              if (settingWithGroup) {
                const groupToCriteria = {
                  'employment-type': 'employmentType',
                  'job-level': 'jobLevel',
                  'organizational-unit': 'organizationalUnit',
                  'location': 'location'
                };
                this.selectedCriteria = groupToCriteria[settingWithGroup.group] || this.selectedCriteria;
              }
            }
            this.stepKey += 1;
            this.$forceUpdate();
          });
        }
      },
      immediate: true
    },
    selectedCriteria: {
      handler(newVal, oldVal) {
        if (this.currentStep === 2 && oldVal && newVal !== oldVal && !this.isEditingMode) {
          const newGroup = this.criteriaOptions.find(c => c.id === newVal)?.value;
          if (newGroup) {
            this.existingPaySettings.forEach(setting => {
              if (setting.name !== 'Independent Contractor') {
                this.$set(setting, 'group', newGroup);
                this.$set(setting, 'groupIds', []);
              }
            });
            this.selectedOptions = [];
          }
        }
      }
    }
  },
  methods: {
    initializePaySettings(settings) {
        const normalizedSettings = [];

      settings.forEach(setting => {
        const normalizedSetting = { ...setting };
        let groupIds = [];
        if (Array.isArray(setting.groupIds)) {
          groupIds = [...setting.groupIds];
        } else if (setting.groupIds) {
          groupIds = [].concat(setting.groupIds);
        }

        if (groupIds.length === 0 && setting.groupValues) {
          if (Array.isArray(setting.groupValues) && setting.groupValues.length > 0) {
            groupIds = setting.groupValues.map(item => {
              if (typeof item === 'object' && item !== null && item.id) {
                return String(item.id);
              }
              if (typeof item === 'string') {
                return item;
              }
              return String(item);
            }).filter(Boolean);
          }
        }

        if (groupIds.length > 0) {
          groupIds = groupIds.map(id => {
            if (typeof id === 'object' && id !== null && id.id) {
              return String(id.id);
            }
            return String(id);
          });
        }

        normalizedSetting.groupIds = groupIds;
        normalizedSettings.push(normalizedSetting);
      });

      this.existingPaySettings = normalizedSettings;
      this.$nextTick(() => {
        normalizedSettings.forEach((setting, index) => {
          if (this.existingPaySettings[index]) {
            this.$set(this.existingPaySettings[index], 'groupIds', setting.groupIds);
          }
        });
      });
    },
    normalizePaySettingsData(settings) {
      return settings.map(setting => {
        let groupIds = [];
        if (Array.isArray(setting.groupIds)) {
          groupIds = [...setting.groupIds];
        } else if (setting.groupIds) {
          groupIds = [].concat(setting.groupIds);
        }

        if (groupIds.length === 0 && setting.groupValues && Array.isArray(setting.groupValues) && setting.groupValues.length > 0) {
          groupIds = setting.groupValues.map(item => {
            if (typeof item === 'object' && item !== null && item.id) {
              return String(item.id);
            }
            return String(item);
          }).filter(Boolean);
        }

        if (groupIds.length > 0) {
          groupIds = groupIds.map(id => {
            if (typeof id === 'object' && id !== null && id.id) {
              return String(id.id);
            }
            return String(id);
          });
        }

        return {
          ...setting,
          groupIds
        };
      });
    },
    togglePaySettingForm() {
      this.isPaySettingFormExpanded = !this.isPaySettingFormExpanded;
    },
      toggleSetting(setting) {
      const settingId = setting.id;
      const currentState = this.expandedSettings[settingId];
      this.$set(this.expandedSettings, settingId, currentState === undefined ? false : !currentState);
    },
    isSettingExpanded(setting) {
      const settingId = setting.id;
      return this.expandedSettings[settingId] !== false;
    },
    toggleInformationBox() {
      this.showInformationBox = !this.showInformationBox;
    },
    handleBack() {
      if (this.currentStep === 2) {
        this.preventWatcherReset = true;
        this.currentStep = 1;
        this.stepKey += 1;
        this.$nextTick(() => {
          this.$forceUpdate();
          setTimeout(() => {
            this.preventWatcherReset = false;
          }, 100);
        });
      } else {
        this.$emit('close');
      }
    },
    handleIndependentContractorToggle(event) {
      this.independentContractorEnabled = event.target.checked;
    },
    handleSubmit() {
      if (this.currentStep === 1) {
        const hasIndependentContractor = this.existingPaySettings.some(s => s.name === 'Independent Contractor');
        if (this.independentContractorEnabled && !hasIndependentContractor) {
          this.savePaySetting();
        } else {
          const currentGroup = this.criteriaOptions.find(c => c.id === this.selectedCriteria)?.value;
          if (currentGroup) {
            this.existingPaySettings.forEach(setting => {
              if (setting.name !== 'Independent Contractor') {
                  const groupChanged = setting.group && setting.group !== currentGroup;
                this.$set(setting, 'group', currentGroup);
                if (groupChanged) {
                  this.$set(setting, 'groupIds', []);
                }
              }
            });
          }

          this.currentStep = 2;
          this.paySettingName = `Pay Group ${this.paySettingsCount}`;
        }
      } else {
        this.savePaySetting();
      }
    },
    getCriteriaName() {
      const criteria = this.criteriaOptions.find(c => c.id === this.selectedCriteria);
      return criteria ? criteria.name.toUpperCase() : '';
    },
    async loadCriteriaData() {
      this.loading = true;
      try {
        const empTypes = await this.$_getHumanarEmploymentTypes();
        this.optionsData.employmentType = empTypes.data.result.map(item => ({
          id: item.id,
          name: item.name
        }));

        const jobLevels = await this.$_getLevels();
        this.optionsData.jobLevel = jobLevels.data.levels.map(item => ({
          id: item.id,
          name: item.name
        }));
        const functions = await this.$_getFunction();
        this.optionsData.organizationalUnit = functions.data.functions.map(item => ({
          id: item.id,
          name: item.name
        }));

        const locations = await this.$_getHumanarLocations();
        this.optionsData.location = locations.data.outlets.map(item => ({
          id: item.id,
          name: item.name
        }));
      } catch (error) {
        this.$toasted.error(error.message || 'Failed to load criteria data', { duration: 5000 });
      } finally {
        this.loading = false;
      }
    },
    getCriteriaOptions() {
      return this.optionsData[this.selectedCriteria] || [];
    },
    isOptionDisabled(optionId) {
      if (this.selectedOptions && this.selectedOptions.includes(optionId)) {
        return false;
      }
      return this.disabledOptionIds.includes(optionId);
    },
    getCriteriaNameById(criteriaId) {
      const criteria = this.criteriaOptions.find(c => c.id === criteriaId);
      return criteria ? criteria.name : '';
    },
    getSelectedOptionsDisplay(selectedIds) {
      const options = this.getCriteriaOptions();
      const selected = selectedIds
        .map(id => {
          const option = options.find(opt => (opt.id || opt) === id);
          return option ? option.name : '';
        })
        .filter(name => name);
      return selected.length > 0 ? selected.join(', ') : 'None';
    },
    isOptionSelectedForSetting(optionId, setting) {
      if (!setting.groupIds || !Array.isArray(setting.groupIds)) {
        return false;
      }
      return setting.groupIds.includes(optionId);
    },
    isOptionDisabledForSetting(optionId, currentSettingId) {
      const currentSetting = this.existingPaySettings.find(s => s.id === currentSettingId);
      if (currentSetting && this.isOptionSelectedForSetting(optionId, currentSetting)) {
        return false;
      }

      const usedByOther = this.existingPaySettings.some(setting => {
        if (setting.id === currentSettingId) return false;
        return this.isOptionSelectedForSetting(optionId, setting);
      });

      const usedInNewForm = this.selectedOptions && this.selectedOptions.includes(optionId);

      if (usedByOther || usedInNewForm) return true;

      return false;
    },
    hasNoSelectedOptions(setting) {
      if (!setting.groupIds || !Array.isArray(setting.groupIds)) {
        return true;
      }
      return setting.groupIds.length === 0;
    },
    async handleDeleteSetting(setting) {
      if (!setting.id) {
        this.$toasted.error('Cannot delete pay group without an ID', { duration: 5000 });
        return;
      }

      if (!this.hasNoSelectedOptions(setting)) {
        this.$toasted.error('Cannot delete pay group with selected options', { duration: 5000 });
        return;
      }

      try {
        this.isLoading = true;
        await this.$_deletePaySettings(setting.id);
        const index = this.existingPaySettings.findIndex(s => s.id === setting.id);
        if (index !== -1) {
          this.existingPaySettings.splice(index, 1);
        }

        this.$toasted.success('Pay group deleted successfully', { duration: 2000 });
        this.$emit('delete', setting.id);
      } catch (error) {
        const errorMessage = error.response?.data?.message || error
        this.$toasted.error(errorMessage, { duration: 5000 });
      } finally {
        this.isLoading = false;
      }
    },
    toggleOptionForSetting(event, optionId, setting) {
      const isChecked = event.target.checked;
      const currentOptions = setting.groupIds || [];
      let newOptions;

      if (isChecked) {
        if (!this.isOptionSelectedForSetting(optionId, setting)) {
          newOptions = [...currentOptions, optionId];
        } else {
          newOptions = currentOptions;
        }
      } else {
        newOptions = currentOptions.filter(opt => opt !== optionId);
      }

      this.$set(setting, 'groupIds', newOptions);
    },
    savePaySetting() {
      this.isLoading = true;

      const hasIndependentContractor = this.existingPaySettings.some(s => s.name === 'Independent Contractor');
      const shouldSaveAsIndependentContractor = this.independentContractorEnabled && !hasIndependentContractor;
      const inStep2 = this.currentStep === 2 || this.isEditingMode;

      let paySettingData;

      if (shouldSaveAsIndependentContractor && !inStep2) {
        paySettingData = {
          name: 'Independent Contractor',
        };
      } else {
        const allPaySettings = [];
        const currentGroup = this.criteriaOptions.find(i => i.id === this.selectedCriteria)?.value;

        this.existingPaySettings.forEach(setting => {
          const groupToUse = setting.name === 'Independent Contractor'
            ? setting.group
            : (currentGroup || setting.group);

          allPaySettings.push({
            id: setting.id,
            name: setting.name,
            group: groupToUse,
            groupIds: setting.groupIds || []
          });
        });

        if (!this.isEditingMode && this.paySettingName.trim() && this.selectedOptions.length > 0) {
          allPaySettings.push({
            id: null,
            name: this.paySettingName,
            group: currentGroup,
            groupIds: this.selectedOptions
          });
        }

        paySettingData = allPaySettings;
      }

      this.isLoading = false;
      this.$emit('save', paySettingData);
    }
  },
  async mounted() {
    await this.loadCriteriaData();

    if (this.existingSettings && this.existingSettings.length > 0) {
      this.initializePaySettings(this.existingSettings);
    }

    const hasIndependentContractor = this.existingPaySettings.some(s => s.name === 'Independent Contractor');
    if (hasIndependentContractor) {
      this.independentContractorEnabled = true;
    }

    if (this.isEditingMode) {
      this.currentStep = 2;

      let group = null;
      if (this.existingPaySettings.length > 0 && this.existingPaySettings[0].group) {
        group = this.existingPaySettings[0].group;
      } else if (this.editingPaySetting && this.editingPaySetting.group) {
        group = this.editingPaySetting.group;
      }

      if (group) {
        const groupToCriteria = {
          'employment-type': 'employmentType',
          'job-level': 'jobLevel',
          'organizational-unit': 'organizationalUnit',
          'location': 'location'
        };
        this.selectedCriteria = groupToCriteria[group] || 'employmentType';
      }
    } else if (this.existingPaySettings.length > 0) {
      const settingWithGroup = this.existingPaySettings.find(s => s.group && s.name !== 'Independent Contractor');
      if (settingWithGroup) {
        const groupToCriteria = {
          'employment-type': 'employmentType',
          'job-level': 'jobLevel',
          'organizational-unit': 'organizationalUnit',
          'location': 'location'
        };
        const newCriteria = groupToCriteria[settingWithGroup.group] || this.selectedCriteria;

        if (this.selectedCriteria !== newCriteria) {
          this.selectedCriteria = newCriteria;
        }

        this.$nextTick(() => {
          this.stepKey += 1;
          this.$forceUpdate();
        });
      }
    }
  }
};
</script>

<style scoped>
.switch {
  position: relative;
  display: inline-block;
  width: 24px;
  height: 12px;
}

.switch input {
  opacity: 0;
  width: 0;
  height: 0;
}

.slider {
  position: absolute;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  background-color: #EEEEEE;
  transition: 0.4s;
  border-radius: 20px;
}

.slider:before {
  position: absolute;
  content: "";
  height: 9.6px;
  width: 9.6px;
  border-radius: 50%;
  left: 2px;
  bottom: 1.5px;
  background-color: #878E99;
  transition: 0.4s;
}

input:checked + .slider {
  background-color: #EEEEEE;
}
input:checked+.slider:before {
  transform: translateX(10px);
  background-color:#13B56A;
}

.switch-header {
  font-size: 10px;
  text-transform: uppercase;
  font-weight: 600;
  color: #878E99;
}

.pay-settings-tabs {
  width: 100%;
}

.border-gray-200 {
  min-height: 100px;
}

.bg-orange-50 {
  line-height: 1.5;
}

@media (max-width: 640px) {
  .grid.grid-cols-2 {
    grid-template-columns: 1fr;
  }
}

.info-box {
  display: flex;
  align-items: center;
  letter-spacing: 0.5px;
  background-color: #E9932314;
  padding: 10px;
  border-radius: 8px;
  font-size: 12px;
  color: #333333;
  border: 1px solid #E993235C;
  margin-bottom: 20px;
}

input[type="checkbox"] {
  width: 18px;
  height: 18px;
  cursor: pointer;
}

input[type="checkbox"]:checked {
  background-color: #f97316;
}

@media (max-width: 768px) {
  .text-sm {
    font-size: 0.875rem;
  }
  .text-lg {
    font-size: 1rem;
  }
}

.custom-scrollbar::-webkit-scrollbar {
  width: 6px;
}

.custom-scrollbar::-webkit-scrollbar-track {
  background: #f1f1f1;
  border-radius: 10px;
}

.custom-scrollbar::-webkit-scrollbar-thumb {
  background: #cbd5e0;
  border-radius: 10px;
}

.custom-scrollbar::-webkit-scrollbar-thumb:hover {
  background: #a0aec0;
}

.bg-white {
  box-shadow: 0 1px 3px 0 rgba(0, 0, 0, 0.1);
}

.btnClass {
  width: 32px;
  height: 32px;
  border-radius: 50%;
}
</style>

